/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: iconRenderer.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Utility component for rendering Lucide icons based on icon name.
────────────────────────────────────────────────────────────────*/

import * as LucideIcons from 'lucide-react';

/**
 * Renders Lucide icons based on icon name
 * @param iconName - Name of the icon to render
 * @param className - CSS classes to apply to the icon
 * @returns JSX element of the icon or null if not found
 */
export function renderIcon(iconName: string, className: string = "w-4 h-4") {
  // Mapping of icon names to Lucide components
  const iconMap: Record<string, any> = {
    'phone': LucideIcons.Phone,
    'bar-chart-3': LucideIcons.BarChart3,
    'settings': LucideIcons.Settings,
    'shield': LucideIcons.Shield,
    'grid-3x3': LucideIcons.Grid3X3,
    'users': LucideIcons.Users,
    'key': LucideIcons.Key,
    'file-text': LucideIcons.FileText,
    'wifi': LucideIcons.Wifi,
    'message-circle': LucideIcons.MessageCircle,
    'bot': LucideIcons.Bot,
    'wrench': LucideIcons.Wrench,
    'server': LucideIcons.Server,
    'building': LucideIcons.Building,
    'plus': LucideIcons.Plus,
    'puzzle': LucideIcons.Puzzle,
    'download': LucideIcons.Download,
    'palette': LucideIcons.Palette,
    'link': LucideIcons.Link,
    'user': LucideIcons.User,
    'music': LucideIcons.Music,
    'mic': LucideIcons.Mic,
    'mail': LucideIcons.Mail,
    'certificate': LucideIcons.FileCheck,
    'list': LucideIcons.List,
    'globe': LucideIcons.Globe,
    'clock': LucideIcons.Clock,
    'calendar': LucideIcons.Calendar,
    'moon': LucideIcons.Moon,
    'coffee': LucideIcons.Coffee,
    'lock': LucideIcons.Lock,
    'map-pin': LucideIcons.MapPin,
    'arrow-right': LucideIcons.ArrowRight,
    'arrow-left': LucideIcons.ArrowLeft,
    'refresh-cw': LucideIcons.RefreshCw,
    'arrow-up': LucideIcons.ArrowUp,
    'crown': LucideIcons.Crown,
    'credit-card': LucideIcons.CreditCard,
    'folder': LucideIcons.Folder,
    'megaphone': LucideIcons.Megaphone,
    'external-link': LucideIcons.ExternalLink,
    'hash': LucideIcons.Hash,
    'hand': LucideIcons.Hand,
    'car': LucideIcons.Car,
    'zap': LucideIcons.Zap,
    'volume-2': LucideIcons.Volume2,
    'phone-call': LucideIcons.PhoneCall,
    'navigation': LucideIcons.Navigation,
    'tag': LucideIcons.Tag,
    'ban': LucideIcons.Ban,
    'code': LucideIcons.Code,
    'cpu': LucideIcons.Cpu,
    'eye': LucideIcons.Eye,
    'activity': LucideIcons.Activity,
    'check-square': LucideIcons.CheckSquare,
    'book': LucideIcons.Book,
    'search': LucideIcons.Search,
    'edit': LucideIcons.Edit,
    'edit-3': LucideIcons.Edit3,
    'alert-triangle': LucideIcons.AlertTriangle,
    'file-template': LucideIcons.FileText,
    'circle': LucideIcons.Circle
  };

  const IconComponent = iconMap[iconName] || LucideIcons.Circle;
  
  return <IconComponent className={className} />;
}

